import { runwayPoint, runwayHeadingError, onRunway, RUNWAY } from '../runwayFrame.js';
import { DEG2RAD } from '../../utils.js';

/**
 * FT-101 — Basic Aircraft Handling.
 *
 * A cold start on the ramp, a short taxi, one full left-hand circuit of runway
 * 35, and a full-stop landing. Every position is authored in the runway frame
 * (see ../runwayFrame.js): `along` metres down the runway from the taxiway
 * intersection, `lateral` metres right of the centreline. The circuit is flown
 * on the west side of the field, matching the Free Flight "Landing Practice"
 * downwind leg so both scenarios teach the same picture.
 *
 * Pattern (all left turns, viewed from above with the runway pointing "up"):
 *
 *        crosswind ●─────────────┐
 *                  │             │  upwind / departure
 *       downwind   │             │
 *           ●──────┘         ┌───┴───┐
 *           │                │ RWY35 │
 *           │       abeam ●  └───┬───┘
 *           │                    │
 *  dwnd end ●─╮              ╭───● final
 *             ╰───── ● ──────╯
 *                   base
 *
 * Voice-over: each objective's `audio` key names its recordings under
 * public/assets/sound/voice/basicHandling/ — `<key>_captions` covers the whole
 * briefing (however many caption lines it's split into), `<key>_complete` the
 * credit line, and `<key>_hint_<id>` each hint. The keys carry the script's
 * running order so the files sort the way the lesson plays, which is why they
 * aren't just the objective ids. Caption `duration`s are relative weights: the
 * recording's real length sets the pace (see training/CaptionPlayer.js).
 */

// ── Circuit geometry ────────────────────────────────────────────────────────
// The circuit is deliberately long in the `along` axis. A student is often still
// climbing (or still hunting for the gear lever) well past the departure end, so
// the crosswind turn has to sit far enough out that it's still ahead of them
// when they level off. Rough timings: crosswind 27 s, downwind 51 s, base turn
// 40 s, final 33 s.
const PATTERN_ALT = 457;   // m MSL (≈1,500 ft) — matches the Landing Practice spawn
const DESCENT_ALT = 420;   // m MSL (≈1,380 ft) — part-way down the downwind leg
const BASE_ALT = 370;      // m MSL (≈1,200 ft) — descending through the base turn
const FINAL_ALT = 250;     // m MSL (≈  800 ft) — rolling out on final
// Width is pinned to the Free Flight "Landing Practice" spawn (along -418,
// lateral -2369), so both scenarios show the same downwind picture. Stretching
// the pattern is therefore an `along`-axis change only.
const DOWNWIND = -2370;    // m right of centreline (negative = west, ≈1.3 NM out)

// Where the downwind leg ends and the turn to final begins. Chosen so the
// threshold sits exactly 45° behind the left wingline — the standard visual cue
// for starting a base turn: from here it's DOWNWIND (2370 m) off the wing and
// the same distance again behind (-3000 + 620 = -2380). Note that this is also
// where the roll-out onto final lands, which isn't a coincidence: a square
// pattern's first turn walks you -R down the field and the second walks you the
// same +R back, so the two share an `along` whatever the turn radius happens to
// be. The base marker is the apex between them (see the objective).
const BASE_TURN = -3000;

// Capture volumes. Airborne legs are deliberately loose — they're a picture to
// fly, not a gate to thread — while the ground points need to be tight enough
// to mean "on the taxiway" and "on the centreline".
const LEG = { radius: 520, vertical: 400 };
const GROUND = { radius: 40, vertical: 50 };

const LINEUP_TOLERANCE = 10 * DEG2RAD;   // how straight "lined up" has to be
// Slack added to the runway box when judging "did they land on it" — enough for
// a wide touchdown or a long float, not enough to count the taxiway or the dirt.
const RUNWAY_MARGIN = 40;

const landedOnRunway = (c) => onRunway(c.aircraft.position.x, c.aircraft.position.z, RUNWAY_MARGIN);

// ── Hint factories ──────────────────────────────────────────────────────────
// The circuit legs share the same two failure modes — drifting off the target
// altitude and losing track of where the next marker is — so they're built
// rather than repeated. Only the thresholds vary per leg: the spoken lines are
// deliberately generic ("profile", not "twelve hundred"; "your next turn point",
// not "downwind") and word-for-word identical everywhere they're used, so the
// whole pattern needs one recording per line rather than one per leg. That's
// what the explicit `audio` keys are for: they point every leg at the one file
// instead of the per-objective name a hint would otherwise resolve to.

function altitudeHints(target) {
  return [
    {
      id: 'high',
      when: (c) => c.altitude > target + 180,
      after: 9, cooldown: 22,
      audio: 'altitudeHints_high',
      caption: "You're too high — ease her back down onto profile.",
    },
    {
      id: 'low',
      when: (c) => c.altitude < target - 180,
      after: 9, cooldown: 22,
      audio: 'altitudeHints_low',
      caption: "You're too low — add a little power and get back up on profile.",
    },
  ];
}

function steerHint() {
  return {
    id: 'steer',
    // Wide off the nose, not merely far away. The legs are kilometres long — a
    // player rolling out on downwind is 6 km from the base corner and flying at
    // it perfectly — so range alone would nag the whole way round the circuit.
    // The `after` window is long enough to sit out an honest ninety-degree turn.
    when: (c) => c.waypointRange > 1500 && Math.abs(c.waypointHeadingError) > 60 * DEG2RAD,
    after: 16, cooldown: 24,
    audio: 'steerHint_steer',
    caption: 'Your next turn point is marked on the HUD — bring the nose round to it.',
  };
}

export default {
  id: 'basicHandling',
  title: 'FT-101 · BASIC AIRCRAFT HANDLING',

  objectives: [
    // ── 1. Intro ────────────────────────────────────────────────────────────
    {
      id: 'intro',
      audio: '01_intro',
      type: 'info',
      objective: '',
      audioCaptions: [
        { caption: 'Hawg One, this is your instructor. Welcome to basic aircraft handling.', duration: 4 },
        { caption: "We'll run a cold start, taxi out, fly one circuit of the pattern, and put her back on the deck.", duration: 6 },
        { caption: "Take your time. I'll call every step.", duration: 3 },
      ],
    },

    // ── 2. Start engines ────────────────────────────────────────────────────
    // Launched from the start screen the jet is cold, so this is a real step.
    // After the first-run intro the engines are already cranking (the intro
    // starts them under its shots — see SimConfig INTRO.engineStart), so the
    // objective is satisfied when it comes up and TrainingMission skips it
    // silently: no row, no briefing, no credit line.
    {
      id: 'startEngines',
      audio: '02_startEngines',
      type: 'task',
      objective: 'Start the engines  [I]',
      audioCaptions: [
        { caption: "You're cleared to crank. Go ahead and start up the engines.", duration: 4.5 },
      ],
      complete: (c) => c.aircraft.engineState.isRunning || c.aircraft.engineState.isStarting,
      hints: [
        // The recorded `hint_nudge` names the old E key. This id has no
        // recording yet, so it plays caption-only until it's re-cut.
        { id: 'nudgeI', after: 12, cooldown: 16, caption: 'Whenever you\'re ready, press I to start the engines.' },
      ],
      onCompleteCaptions: [
        { caption: 'Good. Give them a few seconds to come up to idle.', duration: 3.5 },
      ],
    },

    // ── 3. Controls briefing (plays while the engines spool) ────────────────
    {
      id: 'controlsBrief',
      audio: '03_controlsBrief',
      type: 'info',
      objective: '',
      audioCaptions: [
        { caption: 'While she spools up — quick refresher on the controls.', duration: 3.5 },
        { caption: 'The jet flies where you look. Move the mouse and she rolls into the turn on her own.', duration: 6 },
        { caption: 'Scroll wheel is throttle. F cycles the flaps, G works the gear, and B holds the brakes.', duration: 6.5 },
        { caption: 'Hold space to look around without turning, and press Enter to switch between cockpit and chase view.', duration: 6 },
      ],
      // Don't hand the jet over until the engines are actually turning.
      complete: (c) => c.aircraft.engineState.isRunning,
    },

    // ── 4. Taxi and hold short ──────────────────────────────────────────────
    {
      id: 'taxiHoldShort',
      audio: '04_taxiHoldShort',
      type: 'waypoint',
      objective: 'Taxi to runway 35 and hold short',
      waypoint: { ...runwayPoint(-30.65, 60), label: 'HOLD SHORT', ...GROUND },
      audioCaptions: [
        { caption: 'Alright, engines are up. Roll off the ramp and taxi west to the hold line for runway three-five.', duration: 6 },
        { caption: 'Twenty percent throttle is plenty. Steer with the mouse, and stop at the line.', duration: 5 },
      ],
      // "Hold short" means stopped at the line, not just passing through it.
      complete: (c) => c.groundSpeed < 3,
      hold: 0.5,
      hints: [
        {
          id: 'power',
          when: (c) => c.groundSpeed < 1 && c.controller.throttle < 0.08,
          after: 12, cooldown: 16,
          caption: "She won't roll without power — scroll the throttle up to about twenty percent.",
        },
      ],
      onCompleteCaptions: [
        { caption: 'Holding short. Nicely done.', duration: 2.5 },
      ],
    },

    // ── 5. Line up ──────────────────────────────────────────────────────────
    {
      id: 'lineUp',
      audio: '05_lineUp',
      type: 'waypoint',
      objective: 'Line up on runway three-five',
      waypoint: { ...runwayPoint(0, 0), label: 'LINE UP', radius: 45, vertical: 50 },
      audioCaptions: [
        { caption: "You're cleared onto the runway. Taxi out and swing the nose straight down the centerline.", duration: 6 },
      ],
      complete: (c) => Math.abs(runwayHeadingError(c.aircraft.forwardVector)) < LINEUP_TOLERANCE,
      hints: [
        {
          id: 'straighten',
          when: (c) => c.waypointRange < 60 && Math.abs(runwayHeadingError(c.aircraft.forwardVector)) >= LINEUP_TOLERANCE,
          after: 5, cooldown: 12,
          caption: 'Straighten her out — nose pointed down the runway.',
        },
      ],
    },

    // ── 6. Takeoff ──────────────────────────────────────────────────────────
    {
      id: 'takeoff',
      audio: '06_takeoff',
      type: 'task',
      objective: 'Full throttle and take off',
      audioCaptions: [
        { caption: 'Hawg One, cleared for takeoff. Throttle to the stops.', duration: 4 },
        { caption: "She'll want to fly around a hundred forty knots — ease the nose up and let her come off the deck.", duration: 6 },
      ],
      complete: (c) => c.altitudeAGL > 25,
      hints: [
        {
          id: 'rotate',
          when: (c) => c.grounded && c.airspeedKts > 145,
          after: 1.5, cooldown: 7,
          caption: "Speed's there! Pull the nose up and fly her off.",
        },
      ]
    },

    // ── 7. Gear up ──────────────────────────────────────────────────────────
    // Its own step rather than a clause on the climb: G is a toggle, so a player
    // who cleaned her up on their own the moment the wheels left would otherwise
    // be told to "gear up" with the gear already up — and put it straight back
    // down. Alone, the objective simply never appears.
    {
      id: 'gearUp',
      audio: '07_gearUp',
      type: 'task',
      objective: 'Raise the landing gear  [G]',
      audioCaptions: [
        { caption: 'Positive rate — Press G to raise the gear.', duration: 3.5 },
      ],
      complete: (c) => c.aircraft.gearTarget === 0,
      hints: [
        {
          id: 'gear',
          after: 6, cooldown: 14,
          caption: "Get the gear up, Hawg One!",
        },
      ],
    },

    // ── 8a. Climb out ────────────────────────────────────────────────────────
    {
      id: 'climbOut',
      audio: '08a_climbOut',
      type: 'task',
      objective: 'Climb to 1,500 ft',
      audioCaptions: [
        { caption: "Climb straight ahead to fifteen hundred feet — that's our pattern altitude.", duration: 5 },
      ],
      complete: (c) => c.altitude >= PATTERN_ALT - 10,
      hints: [
        {
          id: 'straight',
          when: (c) => Math.abs(c.bank) > 30 * DEG2RAD && c.altitude < PATTERN_ALT - 60,
          after: 5, cooldown: 15,
          caption: 'Hold your heading — climb straight out before you turn.',
        },
      ],
      onCompleteCaptions: [
        { caption: 'Fifteen hundred. Level her off and hold it.', duration: 3.5 },
      ],
    },

    // ── 8b. Crosswind ────────────────────────────────────────────────────────
    {
      id: 'crosswind',
      audio: '08b_crosswind',
      type: 'waypoint',
      objective: 'Turn left onto crosswind',
      // 3.5 km past the departure end. A student typically reaches pattern
      // altitude somewhere around along 3000–3800 — later still if they're slow
      // to clean her up — so the marker has to sit beyond that to leave any
      // level departure leg at all, instead of arriving abeam the turn.
      waypoint: { ...runwayPoint(5000, -1300, PATTERN_ALT), label: 'CROSSWIND', ...LEG },
      audioCaptions: [
        { caption: "Now roll into a left turn onto crosswind and hold fifteen hundred.", duration: 6 },
      ],
      hints: [steerHint(), ...altitudeHints(PATTERN_ALT)],
    },

    // ── 9. Downwind ─────────────────────────────────────────────────────────
    {
      id: 'downwind',
      audio: '09_downwind',
      type: 'waypoint',
      objective: 'Turn left onto downwind',
      waypoint: { ...runwayPoint(2400, DOWNWIND, PATTERN_ALT), label: 'DOWNWIND', ...LEG },
      audioCaptions: [
        { caption: 'Another ninety to the left, onto downwind.', duration: 3.5 },
        { caption: 'You want the runway sitting off your left wing, about a mile and a half out.', duration: 6.5 },
      ],
      hints: [steerHint(), ...altitudeHints(PATTERN_ALT)],
    },

    // ── 10a. Gear down ──────────────────────────────────────────────────────
    // Split from the flaps for the same reason as the gear-up step: G toggles,
    // so a player who dropped the gear early on downwind must not be told to
    // drop it again. Either half is skipped on its own if already done.
    {
      id: 'gearDown',
      audio: '10a_gearDown',
      type: 'task',
      objective: 'Lower the landing gear  [G]',
      // Guide-only marker: the abeam point tells the player where they are on
      // downwind without gating the configuration change on flying through it.
      waypoint: { ...runwayPoint(RUNWAY.approachEnd, DOWNWIND, PATTERN_ALT), label: 'ABEAM', guide: true, ...LEG },
      audioCaptions: [
        { caption: 'Alright let\'s get her configured for landing. Go ahead and lower the gear.', duration: 5 },
      ],
      complete: (c) => c.aircraft.gearTarget === 1,
      hints: [
        {
          id: 'gear',
          after: 10, cooldown: 16,
          caption: 'Gears still up — press G before you turn base.',
        },
      ],
    },

    // ── 10b. Flaps ──────────────────────────────────────────────────────────
    {
      id: 'flapsDown',
      audio: '10b_flapsDown',
      type: 'task',
      objective: 'Set flaps for landing  [F]',
      waypoint: { ...runwayPoint(RUNWAY.approachEnd, DOWNWIND, PATTERN_ALT), label: 'ABEAM', guide: true, ...LEG },
      audioCaptions: [
        { caption: 'And give me at least one notch of flaps.', duration: 4 },
      ],
      complete: (c) => c.aircraft.flapStage >= 1,
      hints: [
        {
          id: 'flaps',
          after: 10, cooldown: 16,
          caption: 'Flaps, Hawg One — press F.',
        },
      ],
      onCompleteCaptions: [
        { caption: 'Configured. Start letting her down — power back to around forty percent.', duration: 5 },
      ],
    },

    // ── 10c. Fly the downwind leg out ───────────────────────────────────────
    // Without this step the base marker is handed over the moment the flaps
    // come down — a mile off the nose, with most of the downwind leg still to
    // fly, so "turn base" arrives long before there's anything to turn onto.
    // This owns that stretch: hold the heading, get the descent going, and
    // arrive at the point where the threshold is 45° behind the wing. The
    // "turn base" call then lands on the next objective, exactly when it's due.
    {
      id: 'endDownwind',
      audio: '10c_endDownwind',
      type: 'waypoint',
      objective: 'Fly out the downwind leg',
      waypoint: { ...runwayPoint(BASE_TURN, DOWNWIND, DESCENT_ALT), label: 'DOWNWIND END', ...LEG },
      audioCaptions: [
        { caption: "Hold this heading and start letting her down — I'll call the base turn.", duration: 5 },
        { caption: 'Keep an eye over your left shoulder. When the runway sits about forty-five degrees behind the wing, that\'s the cue.', duration: 7 },
      ],
      hints: [steerHint(), ...altitudeHints(DESCENT_ALT)],
    },

    // ── 11. Base ────────────────────────────────────────────────────────────
    // The apex of one continuous 180° descending turn from the end of downwind
    // round onto final: a 1,185 m radius (half the pattern width) puts its 90°
    // point — where the jet is heading square across the centreline, which is
    // what "on base" actually looks like — right here. Sitting it at the apex
    // rather than out on the downwind line means it's always ahead and to the
    // left, never abeam, so the turn can be flown at it instead of around it.
    {
      id: 'base',
      audio: '11_base',
      type: 'waypoint',
      objective: 'Turn left onto base',
      waypoint: { ...runwayPoint(-4200, -1185, BASE_ALT), label: 'BASE', ...LEG },
      audioCaptions: [
        { caption: 'Turn base. Keep the descent going — looking for about twelve hundred feet through the turn.', duration: 6 },
      ],
      hints: [steerHint(), ...altitudeHints(BASE_ALT)],
    },

    // ── 12. Final ───────────────────────────────────────────────────────────
    {
      id: 'finalApproach',
      audio: '12_finalApproach',
      type: 'waypoint',
      objective: 'Turn final  ·  line up with runway 35',
      // The roll-out of the base turn, back at the `along` it started from.
      waypoint: { ...runwayPoint(BASE_TURN, 0, FINAL_ALT), label: 'FINAL', ...LEG },
      audioCaptions: [
        { caption: 'Turn final and get her on the centerline — eight hundred feet, nose on the numbers.', duration: 6 },
      ],
      hints: [steerHint(), ...altitudeHints(FINAL_ALT)],
      onCompleteCaptions: [
        { caption: "Runway's yours, Hawg One. Keep it steady.", duration: 3.5 },
      ],
    },

    // ── 13. Landing ─────────────────────────────────────────────────────────
    {
      id: 'land',
      audio: '13_land',
      type: 'task',
      objective: 'Land on runway 35',
      waypoint: { ...runwayPoint(RUNWAY.approachEnd, 0, RUNWAY.elevation + 15), label: 'RWY 35', guide: true, radius: 400, vertical: 400 },
      audioCaptions: [
        { caption: 'Cleared to land. Pull back the power and hold about a hundred forty knots, keep a little nose-up, and let her settle onto the mains.', duration: 7.5 },
      ],
      complete: (c) => c.grounded && landedOnRunway(c),
      hold: 0.3,
      hints: [
        {
          id: 'sinking',
          when: (c) => c.airborne && c.altitudeAGL < 150 && c.verticalSpeed < -7,
          after: 2.5, cooldown: 9,
          caption: "You're low and coming down fast — add power and ease the nose up.",
        },
        {
          id: 'slow',
          when: (c) => c.airborne && c.airspeedKts < 130,
          after: 3, cooldown: 11,
          caption: "Watch your speed — nose down a touch and feed in some throttle.",
        },
        {
          id: 'high',
          when: (c) => c.airborne && c.altitudeAGL > 320 && c.along > -3600 && c.along < -800,
          after: 4, cooldown: 13,
          caption: "You're high — pull the power back and let her down.",
        },
        {
          id: 'wide',
          when: (c) => c.airborne && c.along > -3200 && Math.abs(c.lateral) > 300,
          after: 4, cooldown: 13,
          caption: "You're off the centerline — walk her back over before you flare.",
        },
        {
          id: 'goAround',
          when: (c) => c.grounded && !landedOnRunway(c),
          after: 2, cooldown: 20,
          caption: "That's not the runway, Hawg One. Power up, go around, and set up for another approach.",
        },
      ],
    },

    // ── 14. Roll out to a stop ──────────────────────────────────────────────
    {
      id: 'fullStop',
      audio: '14_fullStop',
      type: 'task',
      objective: 'Brake to a full stop  [B]',
      audioCaptions: [
        { caption: "Good touchdown. Hold B on the brakes and bring her to a full stop.", duration: 5 },
      ],
      complete: (c) => c.grounded && c.groundSpeed < 1.5,
      hold: 1.0,
      hints: [
        {
          id: 'brake',
          when: (c) => c.groundSpeed > 50,
          after: 10, cooldown: 12,
          caption: 'Get on the brakes and wash that speed off.',
        }
      ],
    },

    // ── 15. Debrief ─────────────────────────────────────────────────────────
    {
      id: 'debrief',
      audio: '15_debrief',
      type: 'info',
      objective: '',
      audioCaptions: [
        { caption: 'Full stop. That\'s one complete circuit, start to finish.', duration: 4.5 },
        { caption: 'Basic aircraft handling complete — nice flying, Hawg One.', duration: 5 },
      ],
    },
  ],
};
